import { Link, useParams } from "@tanstack/react-router";

import type { DecisionRequest } from "../api";
import { decidedSentence, rationaleOf } from "../inbox";

/**
 * A decided request, read-only: the outcome as a sentence, the reason, and a way to
 * the fact it produced. Replaces the form once the request is decided.
 *
 * `unsavedReason` is the reason the person had typed when someone else decided first
 * (409). It is shown beside the record, selectable, so the words are not lost.
 */
export function DecidedRecord({
  request,
  unsavedReason,
}: {
  request: DecisionRequest;
  unsavedReason?: string;
}) {
  const params = useParams({ strict: false }) as { area?: string; module?: string };
  const reason = rationaleOf(request);
  return (
    <div className="space-y-4" data-decided-record>
      <p className="text-base font-medium text-ks-ink">{decidedSentence(request)}</p>

      {reason && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-ks-grey-1">Reason</p>
          <p className="text-sm whitespace-pre-wrap text-ks-ink">{reason}</p>
        </div>
      )}

      {request.fact_id && (
        // A real anchor to the Facts tab, where the policy fact Vox cites now lives.
        // It opens the tab, not the fact: the Facts tab keeps its open row in local
        // state and reads no `fact` link parameter, so promising to open the fact
        // would be a false claim (declared follow-up, KET-6324 review).
        <Link
          to="/app/$area/$module/$tab"
          params={
            {
              area: params.area ?? "nucleus",
              module: params.module ?? "protex",
              tab: "facts",
            } as never
          }
          className="inline-block text-sm font-medium text-ks-purple underline underline-offset-2"
        >
          See it in Facts, under Policy
        </Link>
      )}

      {unsavedReason && (
        <div className="space-y-1 rounded-lg border border-ks-grey-4 p-3">
          <p className="text-xs font-medium text-ks-grey-1">
            Your reason was not saved. It is here so you can copy it.
          </p>
          <p className="text-sm whitespace-pre-wrap text-ks-ink select-all">{unsavedReason}</p>
        </div>
      )}
    </div>
  );
}
